import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { SubmitHandler, useForm } from "react-hook-form"
import { useMutation, useQuery } from "@apollo/client"
import { v4 as uuidv4 } from "uuid"
import { transformLogicData } from "helpers/transformLogicData"
import {
  DiscountExternalRule,
  InputCreateOrUpdateDiscountExternalRule,
  LogicHolder
} from "lib/types/generated/graphql-types"
import { getLogicData } from "helpers/getLogicData"
import { useAppDispatch } from "lib/store"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import { showEditSidebar } from "lib/store/services/editSidebar/slice"
import { RULE_TYPE } from "lib/types/common"
import { getJson } from "helpers/getJson"

import GET_DISCOUNT_EXTERNAL_RULE from "graphql/queries/discount/GetDiscountExternalRule"
import CREATE_OR_UPDATE_DISCOUNT_EXTERNAL_RULE from "graphql/mutations/discount/CreateOrUpdateDiscountExternalRule"

import { Activate, Deactivate, Divider } from "../Shared.styled"

import { BrinkLoader } from "components/Ui/BrinkLoader"
import Box from "components/Ui/Box"
import PageHeader from "components/Ui/Page/PageHeader"
import LogicRules from "components/Discount/LogicRules"
import DuplicateExternalRule from "./DuplicateExternalRule"
import DiscountRuleGeneralUpdateInput from "components/Discount/DiscountRule/DiscountRuleGeneralUpdateInput"
import { ActionDropdownButton } from "components/Ui/Buttons/ActionDropdownButton"
import Status from "components/Ui/Status"
import DiscountRuleTimeLine from "components/Discount/TimeLine/DiscountRuleTimeLine"

import { ReactComponent as CodeIcon } from "images/icons/brackets-curly.svg"
import { ReactComponent as OnIcon } from "images/icons/circle-play.svg"
import { ReactComponent as OffIcon } from "images/icons/circle-minus.svg"
import { ReactComponent as CopyIcon } from "images/icons/copy.svg"
import { Error404Page } from "../../404"
import { Discount } from "lib/types"
import { TimeLineWrapper } from "../DiscountRules/CreateDiscountRule.styled"

export const UpdateExternalRule = () => {
  const { id } = useParams()
  const dispatch = useAppDispatch()
  const [duplicateExternalRule, setDuplicateExternalRule] = useState<boolean>(false)

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm<InputCreateOrUpdateDiscountExternalRule>()

  const { data, loading, refetch } = useQuery(GET_DISCOUNT_EXTERNAL_RULE, {
    variables: { id }
  })

  const rule: DiscountExternalRule = data?.getDiscountExternalRule

  const [logicState, setLogicState] = useState<Discount.LogicState>({
    [uuidv4()]: {
      conditions: [],
      outcomes: []
    }
  })

  useEffect(() => {
    setValue("name", rule?.name)
    setValue("isActive", rule?.isActive)
    setValue("displayNames", rule?.displayNames)
    if (
      rule?.logicRules !== undefined &&
      rule?.logicRules.length > 0
    ) {
      setLogicState({})
      rule?.logicRules.forEach((logicRule: LogicHolder) => {
        setLogicState((prev: Discount.LogicState) => ({
          ...prev,
          ...(getLogicData(logicRule, rule) as Discount.LogicState)
        }))
      })
    }
  }, [rule])

  const isStackable = watch("isStackable")

  const [addDiscountExternalRule, { loading: createLoading }] = useMutation(
    CREATE_OR_UPDATE_DISCOUNT_EXTERNAL_RULE,
    {
      onCompleted: () => {
        refetch()
        dispatch(
          alertActions.actions.createAlert({
            message: "External rule successfully updated",
            type: "success"
          })
        )
      },
      onError: () => {
        dispatch(
          alertActions.actions.createAlert({
            type: "error"
          })
        )
      }
    }
  )

  const onSubmit: SubmitHandler<InputCreateOrUpdateDiscountExternalRule> = (data) => {
    const {
      name,
      isActive,
      isStackable,
      applyLast,
      isExclusive,
      sortOrder,
      validDateRange,
      displayNames
    } = data

    const transformedData = transformLogicData(logicState)
    addDiscountExternalRule({
      variables: {
        id,
        input: {
          name,
          displayNames,
          isActive: isActive ?? false,
          isStackable: isStackable ?? false,
          applyLast: isStackable ? (applyLast ?? false) : false,
          isExclusive: isStackable ? (isExclusive ?? false) : false,
          sortOrder,
          ...transformedData,
          ...(validDateRange?.from &&
            validDateRange.to && {
              validDateRange: {
                from: new Date(validDateRange.from),
                to: new Date(validDateRange.to)
              }
            })
        }
      }
    })
  }

  const updateActive = (status: boolean) => {
    const { id: _id, ...rest } = rule ?? {}
    addDiscountExternalRule({
      variables: {
        id: rule.id,
        input: {
          ...rest,
          isActive: status
        }
      }
    })
  }

  const watchedName = watch("name")
  const watchedValidFrom = watch("validDateRange.from")
  const watchedValidTo = watch("validDateRange.to")
  const watchedIsStackable = watch("isStackable")
  const watchedIsExclusive = watch("isExclusive")
  const watchedApplyLast = watch("applyLast")
  const watchedSortOrder = watch("sortOrder")

  useEffect(() => {
    if (isStackable === undefined) {
      setValue("isStackable", rule?.isStackable)
      return
    }
    if (!isStackable) {
      setValue("isExclusive", false)
      setValue("applyLast", false)
    }
  }, [isStackable, rule, setValue])

  if (loading) return <BrinkLoader />
  if (!rule) return <Error404Page />

  return (
    <>
      {duplicateExternalRule && (
        <DuplicateExternalRule
          externalRuleData={rule}
          logicState={logicState}
          setDuplicateExternalRule={setDuplicateExternalRule}
        />
      )}
      <Box padding="0" margin="0">
        <form onSubmit={handleSubmit(onSubmit)}>
          <PageHeader
            title={
              <>
                {rule.name}
                {rule.isActive ? (
                  <Status status="Active" />
                ) : (
                  <Status status="Inactive" />
                )}
              </>
            }
            group="External rule"
            id={id}
            hasTabs
            goBackLinkUrl="/discounts/discount-rules"
            goBackLinkText="Back to discount rules"
          >
            <ActionDropdownButton
              title="Save"
              submitEvent={handleSubmit(onSubmit)}
              submitLoading={createLoading}
            >
              <li
                onClick={() => {
                  setDuplicateExternalRule(true)
                  dispatch(showEditSidebar())
                }}
              >
                <CopyIcon /> Duplicate
              </li>
              <li
                onClick={() => {
                  getJson(rule)
                }}
              >
                <CodeIcon /> Get rule JSON
              </li>
              {rule.isActive ? (
                <>
                  <Divider />
                  <Deactivate
                    onClick={() => {
                      updateActive(false)
                    }}
                  >
                    <OffIcon /> Deactivate rule
                  </Deactivate>
                </>
              ) : (
                <>
                  <Divider />
                  <Activate
                    onClick={() => {
                      updateActive(true)
                    }}
                  >
                    <OnIcon /> Activate rule
                  </Activate>
                </>
              )}
            </ActionDropdownButton>
          </PageHeader>
          <Tabs>
            <TabList>
              <Tab>General</Tab>
              <Tab>Logic rules</Tab>
            </TabList>
            <TabPanel>
              <DiscountRuleGeneralUpdateInput
                discountRule={rule}
                control={control}
                isStackable={isStackable}
                errors={errors}
                type={RULE_TYPE.EXTERNAL_RULE}
                setValue={setValue}
                watch={watch}
              />
              <TimeLineWrapper>
                {(watchedValidTo || rule.validDateRange?.to) && (
                  <DiscountRuleTimeLine
                    key={(watchedValidTo ?? rule.validDateRange?.to)?.toString()}
                    previewRule={{
                      name: watchedName ?? rule.name,
                      validFrom: watchedValidFrom ?? rule.validDateRange?.from,
                      validTo: watchedValidTo ?? rule.validDateRange?.to,
                      ruleType: "external",
                      sortOrder: watchedSortOrder ?? rule.sortOrder ?? 0,
                      isStackable: watchedIsStackable ?? rule.isStackable ?? false,
                      isExclusive: watchedIsExclusive ?? rule.isExclusive ?? false,
                      applyLast: watchedApplyLast ?? rule.applyLast ?? false,
                    }}
                  />
                )}
              </TimeLineWrapper>
            </TabPanel>
            <TabPanel>
              <LogicRules state={logicState} setState={setLogicState} />
            </TabPanel>
          </Tabs>
        </form>
      </Box>
    </>
  )
}
